import type { AssemblyMateDocumentJSON } from '../../assembly'
import {
  areAssemblyNodeIdentityEquivalent,
  normalizeAssemblyNodeId,
  splitAssemblyNodePath,
} from '../../assembly/utils/AssemblyNodePath'
import type { GlbHierarchyNode } from '../../imported-model/loaders/glb'

/**
 * 项目模型 `.puppy` 普通节点 alias 创建输入。
 */
export interface CreateProjectModelPuppyNodeAliasByNodeIdInput {
  /** `.puppy` 文档记录的装配根 ID；普通 datum 扩展会借用 Mate 文档根补齐身份。 */
  readonly documentAssemblyId?: unknown
  /** 当前离线包导入后的 hierarchy 根。 */
  readonly hierarchy: GlbHierarchyNode
  /** 当前离线包挂载到 OpenProject 后的目标项目节点 ID。 */
  readonly targetRootAssemblyId: string
}

/**
 * 项目模型 `.puppy` Mate 节点 alias 创建输入。
 */
export interface CreateProjectModelPuppyMateNodeAliasByNodeIdInput {
  /** `.puppy` 包内 Mate 文档。 */
  readonly document: AssemblyMateDocumentJSON
  /** 当前离线包导入后的 hierarchy 根。 */
  readonly hierarchy: GlbHierarchyNode
  /** 当前离线包挂载到 OpenProject 后的目标项目节点 ID。 */
  readonly targetRootAssemblyId: string
  /** scoped 子树恢复时的目标节点；根装配恢复不启用该推断。 */
  readonly scopedTargetRootAssemblyId?: string
}

/**
 * 去重字符串列表。
 *
 * @param values 候选字符串列表。
 * @returns 去掉空字符串和重复项后的列表。
 */
function uniqueStrings(values: readonly string[]): string[] {
  return values.filter((value) => value.length > 0)
    .filter((value, index, array) => array.indexOf(value) === index)
}

/**
 * 规范化项目模型 `.puppy` 节点 ID。
 *
 * @param value 原始节点 ID。
 * @returns 可用于 alias 匹配的节点 ID。
 */
function normalizeProjectModelPuppyNodeId(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * 规范化项目模型 `.puppy` Mate 节点 ID。
 *
 * @param value 原始节点 ID。
 * @returns 可用于 Mate alias 匹配的节点 ID。
 */
function normalizeProjectModelMateNodeId(value: unknown): string {
  return typeof value === 'string' ? normalizeAssemblyNodeId(value.trim()) : ''
}

/**
 * 判断未知值是否为普通记录。
 *
 * @param value 待判断值。
 * @returns 可安全读取字段时返回 true。
 */
function isProjectModelMateRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

/**
 * 写入节点 alias。
 *
 * 设计说明：
 * 同一源节点可能从文档根和 hierarchy meta 两条链路重复推断出来，目标一致时允许覆盖；
 * 目标不一致说明旧包身份本身存在歧义，必须直接抛错，避免 Mate 或 datum 引用恢复到错误组件。
 *
 * @param aliasByNodeId alias 输出表。
 * @param sourceNodeId 源节点 ID。
 * @param targetNodeId 目标节点 ID。
 */
function setProjectModelPuppyNodeAlias(
  aliasByNodeId: Record<string, string>,
  sourceNodeId: string,
  targetNodeId: string,
): void {
  const normalizedSourceNodeId = sourceNodeId.trim()
  const normalizedTargetNodeId = targetNodeId.trim()
  if (
    !normalizedSourceNodeId
    || !normalizedTargetNodeId
    || normalizedSourceNodeId === normalizedTargetNodeId
  ) {
    return
  }
  const currentTargetNodeId = aliasByNodeId[normalizedSourceNodeId]
  if (currentTargetNodeId && currentTargetNodeId !== normalizedTargetNodeId) {
    throw new Error(
      `PuppyCAD 项目模型 .puppy 节点 alias 冲突：${normalizedSourceNodeId} -> ${currentTargetNodeId} / ${normalizedTargetNodeId}`,
    )
  }
  aliasByNodeId[normalizedSourceNodeId] = normalizedTargetNodeId
}

/**
 * 把普通离线包节点 ID 解析为项目模型目标节点 ID。
 *
 * @param sourceNodeId 离线包节点 ID。
 * @param sourceRootAssemblyIds 可作为源根的离线包根 ID。
 * @param targetRootAssemblyId 项目模型目标根 ID。
 * @returns 需要写入 alias 的目标 ID；不属于任何源根时返回 null。
 */
function resolveProjectModelPuppyNodeAliasTarget(
  sourceNodeId: unknown,
  sourceRootAssemblyIds: readonly string[],
  targetRootAssemblyId: string,
): string | null {
  const normalizedSourceNodeId = normalizeProjectModelPuppyNodeId(sourceNodeId)
  if (!normalizedSourceNodeId) {
    return null
  }

  /**
   * scoped 切换后，hierarchy 可能已经被外层流程预拼成 `targetRoot/包内根/子节点`。
   * 这类路径虽然以 targetRoot 开头，但仍然代表包内根残留，必须优先按 source root 折叠。
   */
  const matchedRootAssemblyId = [...sourceRootAssemblyIds]
    .sort((left, right) => right.length - left.length)
    .find((sourceRootAssemblyId) => (
      normalizedSourceNodeId === sourceRootAssemblyId
      || normalizedSourceNodeId.startsWith(`${sourceRootAssemblyId}/`)
    ))

  if (!matchedRootAssemblyId) {
    if (
      normalizedSourceNodeId === targetRootAssemblyId
      || normalizedSourceNodeId.startsWith(`${targetRootAssemblyId}/`)
    ) {
      return null
    }
    return null
  }

  return normalizedSourceNodeId === matchedRootAssemblyId
    ? targetRootAssemblyId
    : `${targetRootAssemblyId}${normalizedSourceNodeId.slice(matchedRootAssemblyId.length)}`
}

/**
 * 收集普通 `.puppy` 数据的源根节点 ID。
 *
 * @param documentAssemblyId `.puppy` 文档级装配根 ID。
 * @param hierarchy 当前离线包 hierarchy。
 * @returns 可用于普通 alias 的源根节点 ID。
 */
function resolveProjectModelPuppySourceRootAssemblyIds(
  documentAssemblyId: unknown,
  hierarchy: GlbHierarchyNode,
): readonly string[] {
  return uniqueStrings([
    normalizeProjectModelPuppyNodeId(documentAssemblyId),
    normalizeProjectModelPuppyNodeId(hierarchy.meta?.assemblyNodeId),
  ])
}

/**
 * 收集 hierarchy 中的普通 `.puppy` 节点 alias。
 *
 * @param node 当前 hierarchy 节点。
 * @param sourceRootAssemblyIds 可作为源根的离线包根 ID。
 * @param targetRootAssemblyId 项目模型目标节点 ID。
 * @param aliasByNodeId alias 输出表。
 */
function collectProjectModelPuppyNodeAliasesFromHierarchy(
  node: GlbHierarchyNode,
  sourceRootAssemblyIds: readonly string[],
  targetRootAssemblyId: string,
  aliasByNodeId: Record<string, string>,
): void {
  const assemblyNodeId = normalizeProjectModelPuppyNodeId(node.meta?.assemblyNodeId)
  const targetNodeId = resolveProjectModelPuppyNodeAliasTarget(
    assemblyNodeId,
    sourceRootAssemblyIds,
    targetRootAssemblyId,
  )
  if (targetNodeId) {
    setProjectModelPuppyNodeAlias(aliasByNodeId, assemblyNodeId, targetNodeId)
  }
  node.children.forEach((child) => {
    collectProjectModelPuppyNodeAliasesFromHierarchy(
      child,
      sourceRootAssemblyIds,
      targetRootAssemblyId,
      aliasByNodeId,
    )
  })
}

/**
 * 收集装配节点路径的逐级前缀。
 *
 * 设计说明：
 * 切换子装配版本时，`.puppy` Mate 实体可能保留“旧包根/当前子装配 occurrence/零件”的完整路径。
 * alias 不能只看文档根，否则会把旧包根直接映射到目标子装配，导致实体路径多出一层 occurrence。
 *
 * @param value Mate 文档中的节点引用。
 * @returns 从根到当前引用的所有路径前缀。
 */
function collectProjectModelMateNodePathPrefixes(value: unknown): readonly string[] {
  const normalizedNodeId = normalizeProjectModelMateNodeId(value)
  if (!normalizedNodeId) {
    return []
  }

  const segments = splitAssemblyNodePath(normalizedNodeId)
  return uniqueStrings(segments.map((_, index) => segments.slice(0, index + 1).join('/')))
}

/**
 * 收集当前项目模型目标根可用于身份匹配的候选 ID。
 *
 * @param targetRootAssemblyId 当前 scoped 切换目标节点。
 * @param hierarchy 离线包 hierarchy 根节点。
 * @returns 可用于从旧 Mate 路径中识别当前子装配 occurrence 的候选 ID。
 */
function collectProjectModelMateTargetRootCandidateNodeIds(
  targetRootAssemblyId: string,
  hierarchy: GlbHierarchyNode,
): readonly string[] {
  const targetRootSegments = splitAssemblyNodePath(targetRootAssemblyId)
  const targetRootSuffixes = targetRootSegments.map((_, index) => targetRootSegments.slice(index).join('/'))

  return uniqueStrings([
    normalizeProjectModelMateNodeId(targetRootAssemblyId),
    ...targetRootSuffixes.map(normalizeProjectModelMateNodeId),
    normalizeProjectModelMateNodeId(hierarchy.meta?.assemblyNodeId),
    normalizeProjectModelMateNodeId(hierarchy.path),
    normalizeProjectModelMateNodeId(hierarchy.name),
  ])
}

/**
 * 从 Mate 文档引用中推断当前子装配在旧包内的 occurrence 根。
 *
 * @param documentReferenceNodeIds Mate 文档中出现过的节点引用。
 * @param hierarchy 当前离线包 hierarchy 根节点。
 * @param targetRootAssemblyId 当前项目模型目标节点。
 * @returns 旧包中代表当前子装配 occurrence 的源根 ID。
 */
function collectProjectModelMateScopedOccurrenceSourceRootIds(
  documentReferenceNodeIds: readonly string[],
  hierarchy: GlbHierarchyNode,
  targetRootAssemblyId: string,
): readonly string[] {
  const targetRootCandidateNodeIds = collectProjectModelMateTargetRootCandidateNodeIds(
    targetRootAssemblyId,
    hierarchy,
  )
  if (targetRootCandidateNodeIds.length === 0) {
    return []
  }

  return uniqueStrings(documentReferenceNodeIds.flatMap((referenceNodeId) => (
    collectProjectModelMateNodePathPrefixes(referenceNodeId).filter((sourceRootAssemblyId) => (
      sourceRootAssemblyId !== normalizeProjectModelMateNodeId(targetRootAssemblyId)
      && targetRootCandidateNodeIds.some((targetRootCandidateNodeId) => (
        areAssemblyNodeIdentityEquivalent(sourceRootAssemblyId, targetRootCandidateNodeId)
      ))
    ))
  )))
}

/**
 * 收集单个 Mate 实体中参与装配节点定位的字段。
 *
 * @param entity Mate 实体引用。
 * @returns 实体内出现过的装配节点引用。
 */
function collectProjectModelMateEntityReferenceNodeIds(entity: unknown): readonly string[] {
  if (!isProjectModelMateRecord(entity)) {
    return []
  }

  const topologyRef = isProjectModelMateRecord(entity.topologyRef) ? entity.topologyRef : null
  const referenceDependency = isProjectModelMateRecord(entity.referenceDependency)
    ? entity.referenceDependency
    : null
  const rawDriverComponentInstanceIds = referenceDependency?.driverComponentInstanceIds
  const rawDriverTopologyRefs = referenceDependency?.driverTopologyRefs
  const driverComponentInstanceIds = Array.isArray(rawDriverComponentInstanceIds)
    ? rawDriverComponentInstanceIds
    : []
  const driverTopologyRefs = Array.isArray(rawDriverTopologyRefs)
    ? rawDriverTopologyRefs
    : []

  return uniqueStrings([
    normalizeProjectModelMateNodeId(entity.assemblyNodeId),
    normalizeProjectModelMateNodeId(entity.componentInstanceId),
    normalizeProjectModelMateNodeId(entity.stableRef),
    normalizeProjectModelMateNodeId(topologyRef?.assemblyNodeId),
    ...driverComponentInstanceIds.map(normalizeProjectModelMateNodeId),
    ...driverTopologyRefs.map((ref) => (
      isProjectModelMateRecord(ref) ? normalizeProjectModelMateNodeId(ref.assemblyNodeId) : ''
    )),
  ])
}

/**
 * 收集 Mate 文档中真实出现过的装配节点引用。
 *
 * @param document `.puppy` Mate 文档。
 * @returns 文档中出现过的装配节点引用。
 */
function collectProjectModelMateDocumentReferenceNodeIds(
  document: AssemblyMateDocumentJSON,
): readonly string[] {
  const components = isProjectModelMateRecord(document.components) ? document.components : {}
  const mates = Array.isArray(document.mates) ? document.mates : []

  return uniqueStrings([
    normalizeProjectModelMateNodeId(document.assemblyId),
    ...Object.keys(components).map(normalizeProjectModelMateNodeId),
    ...Object.values(components).map((component) => (
      isProjectModelMateRecord(component)
        ? normalizeProjectModelMateNodeId(component.componentInstanceId)
        : ''
    )),
    ...mates.flatMap((mate) => {
      if (!isProjectModelMateRecord(mate)) {
        return []
      }

      return [
        normalizeProjectModelMateNodeId(mate.assemblyId),
        ...collectProjectModelMateEntityReferenceNodeIds(mate.firstEntity),
        ...collectProjectModelMateEntityReferenceNodeIds(mate.secondEntity),
        ...collectProjectModelMateEntityReferenceNodeIds(mate.referenceEntity),
      ]
    }),
  ])
}

/**
 * 判断 Mate 文档引用是否使用了候选根前缀。
 *
 * @param referenceNodeId Mate 文档内的节点引用。
 * @param sourceRootAssemblyId 候选根节点 ID。
 * @returns 引用位于候选根下或稳定引用以前缀形式使用候选根时返回 true。
 */
function isProjectModelMateReferenceUsingSourceRoot(
  referenceNodeId: string,
  sourceRootAssemblyId: string,
): boolean {
  const normalizedReferenceNodeId = normalizeProjectModelMateNodeId(referenceNodeId)
  const normalizedSourceRootAssemblyId = normalizeProjectModelMateNodeId(sourceRootAssemblyId)
  if (!normalizedReferenceNodeId || !normalizedSourceRootAssemblyId) {
    return false
  }

  return normalizedReferenceNodeId === normalizedSourceRootAssemblyId
    || normalizedReferenceNodeId.startsWith(`${normalizedSourceRootAssemblyId}/`)
    || normalizedReferenceNodeId.startsWith(`${normalizedSourceRootAssemblyId}:`)
}

/**
 * 解析项目模型 Mate 文档的源根节点 ID。
 *
 * @param document `.puppy` Mate 文档。
 * @param hierarchy 当前节点包 hierarchy。
 * @param targetRootAssemblyId 当前项目模型目标节点。
 * @returns 按优先级去重后的源根节点 ID。
 */
function resolveProjectModelMateSourceRootAssemblyIds(
  document: AssemblyMateDocumentJSON,
  hierarchy: GlbHierarchyNode,
  scopedTargetRootAssemblyId?: string,
): readonly string[] {
  const documentReferenceNodeIds = collectProjectModelMateDocumentReferenceNodeIds(document)
  const scopedOccurrenceRootIds = scopedTargetRootAssemblyId
    ? collectProjectModelMateScopedOccurrenceSourceRootIds(
      documentReferenceNodeIds,
      hierarchy,
      scopedTargetRootAssemblyId,
    )
    : []
  const conditionalHierarchyRootIds = uniqueStrings([
    normalizeProjectModelMateNodeId(hierarchy.path),
    normalizeProjectModelMateNodeId(hierarchy.name),
  ]).filter((sourceRootAssemblyId) => (
    documentReferenceNodeIds.some((referenceNodeId) => (
      isProjectModelMateReferenceUsingSourceRoot(referenceNodeId, sourceRootAssemblyId)
    ))
  ))

  return uniqueStrings([
    normalizeProjectModelMateNodeId(document.assemblyId),
    normalizeProjectModelMateNodeId(hierarchy.meta?.assemblyNodeId),
    ...scopedOccurrenceRootIds,
    ...conditionalHierarchyRootIds,
  ])
}

/**
 * 把 Mate 离线包节点 ID 解析为项目模型目标节点 ID。
 *
 * @param sourceNodeId 离线包节点 ID。
 * @param sourceRootAssemblyIds 可作为源根的离线包根 ID。
 * @param targetRootAssemblyId 项目模型目标根 ID。
 * @returns 需要写入 alias 的目标 ID；不属于任何源根时返回 null。
 */
function resolveProjectModelMateNodeAliasTarget(
  sourceNodeId: unknown,
  sourceRootAssemblyIds: readonly string[],
  targetRootAssemblyId: string,
): string | null {
  const normalizedSourceNodeId = normalizeProjectModelMateNodeId(sourceNodeId)
  const normalizedTargetRootAssemblyId = normalizeProjectModelMateNodeId(targetRootAssemblyId)
  if (!normalizedSourceNodeId) {
    return null
  }

  /**
   * Mate 与普通节点使用同一条折叠语义：`targetRoot/包内根/子节点`
   * 不是合法最终身份，需要继续归一成 `targetRoot/子节点`。
   */
  const matchedRootAssemblyId = [...sourceRootAssemblyIds]
    .sort((left, right) => right.length - left.length)
    .find((sourceRootAssemblyId) => (
      normalizedSourceNodeId === sourceRootAssemblyId
      || normalizedSourceNodeId.startsWith(`${sourceRootAssemblyId}/`)
    ))

  if (!matchedRootAssemblyId) {
    if (
      normalizedSourceNodeId === normalizedTargetRootAssemblyId
      || normalizedSourceNodeId.startsWith(`${normalizedTargetRootAssemblyId}/`)
    ) {
      return null
    }
    return null
  }

  return normalizedSourceNodeId === matchedRootAssemblyId
    ? normalizedTargetRootAssemblyId
    : `${normalizedTargetRootAssemblyId}${normalizedSourceNodeId.slice(matchedRootAssemblyId.length)}`
}

/**
 * 收集离线包 hierarchy 中的 Mate 装配节点引用 alias。
 *
 * @param node 离线包导入 hierarchy 节点。
 * @param sourceRootAssemblyIds 可作为源根的离线包根 ID。
 * @param targetRootAssemblyId 项目模型目标节点 ID。
 * @param aliasByNodeId alias 输出表。
 */
function collectProjectModelMateNodeAliasesFromHierarchy(
  node: GlbHierarchyNode,
  sourceRootAssemblyIds: readonly string[],
  targetRootAssemblyId: string,
  aliasByNodeId: Record<string, string>,
): void {
  const assemblyNodeId = normalizeProjectModelMateNodeId(node.meta?.assemblyNodeId)
  const targetNodeId = resolveProjectModelMateNodeAliasTarget(
    assemblyNodeId,
    sourceRootAssemblyIds,
    targetRootAssemblyId,
  )
  if (targetNodeId) {
    setProjectModelPuppyNodeAlias(aliasByNodeId, assemblyNodeId, targetNodeId)
  }
  node.children.forEach((child) => {
    collectProjectModelMateNodeAliasesFromHierarchy(
      child,
      sourceRootAssemblyIds,
      targetRootAssemblyId,
      aliasByNodeId,
    )
  })
}

/**
 * 创建项目模型 `.puppy` 包内节点到当前项目 nodeKey 的 alias 表。
 *
 * @param input alias 创建输入。
 * @returns 可交给 cad-core 重映射工具的 alias 表。
 */
export function createProjectModelPuppyNodeAliasByNodeId(
  input: CreateProjectModelPuppyNodeAliasByNodeIdInput,
): Record<string, string> {
  const aliasByNodeId: Record<string, string> = {}
  const sourceRootAssemblyIds = resolveProjectModelPuppySourceRootAssemblyIds(
    input.documentAssemblyId,
    input.hierarchy,
  )
  sourceRootAssemblyIds.forEach((sourceRootAssemblyId) => {
    setProjectModelPuppyNodeAlias(aliasByNodeId, sourceRootAssemblyId, input.targetRootAssemblyId)
  })
  collectProjectModelPuppyNodeAliasesFromHierarchy(
    input.hierarchy,
    sourceRootAssemblyIds,
    input.targetRootAssemblyId,
    aliasByNodeId,
  )
  return aliasByNodeId
}

/**
 * 创建项目模型 `.puppy` Mate 文档恢复所需的节点 alias 表。
 *
 * @param input alias 创建输入。
 * @returns 可交给 Mate 文档重映射工具的 alias 表。
 */
export function createProjectModelPuppyMateNodeAliasByNodeId(
  input: CreateProjectModelPuppyMateNodeAliasByNodeIdInput,
): Record<string, string> {
  const aliasByNodeId: Record<string, string> = {}
  const sourceRootAssemblyIds = resolveProjectModelMateSourceRootAssemblyIds(
    input.document,
    input.hierarchy,
    input.scopedTargetRootAssemblyId,
  )
  sourceRootAssemblyIds.forEach((sourceRootAssemblyId) => {
    setProjectModelPuppyNodeAlias(aliasByNodeId, sourceRootAssemblyId, input.targetRootAssemblyId)
  })
  collectProjectModelMateNodeAliasesFromHierarchy(
    input.hierarchy,
    sourceRootAssemblyIds,
    input.targetRootAssemblyId,
    aliasByNodeId,
  )
  return aliasByNodeId
}
